import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { useCurrency } from '../../context/CurrencyContext';
import { 
  Package, AlertTriangle, Check, Edit2, Save, X, Search,
  TrendingDown, PackageOpen, RefreshCw, Filter, Upload, Camera,
  Loader2, Sparkles, Wine, Coffee, Beer, GlassWater, Plus, 
  ShoppingCart, Receipt, CreditCard, Percent, Trash2, Image, Martini
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';

export default function StockManagement() {
  const { t, i18n } = useTranslation();
  const { api } = useAuth();
  const { selectedRestaurant } = useRestaurant();
  const fileInputRef = useRef(null);
  const isSpanish = i18n.language === 'es';
  const { formatPrice, currencyConfig } = useCurrency();
  
  // Stock state
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  
  // AI Scan state
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [scanImage, setScanImage] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [aiCredits, setAiCredits] = useState({ used: 0, limit: 2, purchased: 0 });
  
  // Drinks catalog state
  const [showDrinksDialog, setShowDrinksDialog] = useState(false);
  const [drinksCategory, setDrinksCategory] = useState('soft');
  const [selectedDrinks, setSelectedDrinks] = useState([]);
  const [addingDrinks, setAddingDrinks] = useState(false);
  const [catalogMode, setCatalogMode] = useState('stock'); // 'stock' or 'menu'
  const [showCustomItemDialog, setShowCustomItemDialog] = useState(false);
  const [customItem, setCustomItem] = useState({ name: '', category: '', size: '', price: '' });
  
  // Tax management state
  const [showTaxDialog, setShowTaxDialog] = useState(false);
  const [taxRates, setTaxRates] = useState([]);
  const [editingTax, setEditingTax] = useState(null);

  // AI Credits dialog
  const [showCreditsDialog, setShowCreditsDialog] = useState(false);

  // Drink editing state (full edit - name, category, sizes, image)
  const [showEditDrinkDialog, setShowEditDrinkDialog] = useState(false);
  const [editingDrinkItem, setEditingDrinkItem] = useState(null);
  const [editDrinkForm, setEditDrinkForm] = useState({
    name: '',
    category: '',
    sizes: '',
    image: ''
  });
  const [savingDrink, setSavingDrink] = useState(false);
  const [customCatalogItems, setCustomCatalogItems] = useState({});
  const [globalCatalog, setGlobalCatalog] = useState(null);

  // Drink image editing state
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [editingDrink, setEditingDrink] = useState(null);
  const [drinkImageUrl, setDrinkImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [customDrinkImages, setCustomDrinkImages] = useState({});
  const drinkImageInputRef = useRef(null);
  const editDrinkImageInputRef = useRef(null);

  useEffect(() => {
    if (selectedRestaurant) {
      fetchInventory();
      fetchAiCredits();
      fetchTaxRates();
      fetchDrinkImages();
      fetchCustomCatalogItems();
      fetchGlobalDrinksCatalog();
    }
  }, [selectedRestaurant]);

  // Fetch global drinks catalog (set by super admin)
  const fetchGlobalDrinksCatalog = async () => {
    try {
      const res = await api.get('/admin/drinks-catalog');
      if (res.data?.catalog) {
        setGlobalCatalog(res.data.catalog);
      }
    } catch (error) {
      // Non-admin users will get 403, which is fine - they use default catalog
      console.log('Using default drinks catalog');
    }
  };

  // Fetch custom drink images
  const fetchDrinkImages = async () => {
    try {
      const res = await api.get(`/restaurants/${selectedRestaurant.id}/drink-images`);
      if (res.data?.images) {
        setCustomDrinkImages(res.data.images);
      }
    } catch (error) {
      // Silently fail - images are optional
      console.log('No custom drink images found');
    }
  };

  // Fetch custom catalog items (admin edits/additions)
  const fetchCustomCatalogItems = async () => {
    try {
      const res = await api.get(`/restaurants/${selectedRestaurant.id}/drink-catalog-items`);
      if (res.data?.items) {
        setCustomCatalogItems(res.data.items);
      }
    } catch (error) {
      console.log('No custom catalog items found');
    }
  };

  // Open edit dialog for a drink item
  const handleEditDrink = (drink, categoryKey) => {
    setEditingDrinkItem({ ...drink, categoryKey });
    setEditDrinkForm({
      name: drink.name,
      category: drink.category || '',
      sizes: (drink.sizes || []).join(', '),
      image: customDrinkImages[drink.name] || drink.image || ''
    });
    setShowEditDrinkDialog(true);
  };

  // Save edited drink item
  const handleSaveEditedDrink = async () => {
    if (!editingDrinkItem || !editDrinkForm.name.trim()) {
      toast.error(isSpanish ? 'El nombre es obligatorio' : 'Name is required');
      return;
    }

    setSavingDrink(true);
    try {
      const sizesArray = editDrinkForm.sizes
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      await api.post(`/restaurants/${selectedRestaurant.id}/drink-catalog-items`, {
        original_name: editingDrinkItem.name,
        name: editDrinkForm.name,
        category: editDrinkForm.category,
        sizes: sizesArray,
        image: editDrinkForm.image,
        category_key: editingDrinkItem.categoryKey
      });

      // Update local state
      setCustomCatalogItems(prev => ({
        ...prev,
        [editingDrinkItem.name]: {
          name: editDrinkForm.name,
          category: editDrinkForm.category,
          sizes: sizesArray,
          image: editDrinkForm.image
        }
      }));

      // Also update the image in customDrinkImages
      if (editDrinkForm.image) {
        setCustomDrinkImages(prev => ({
          ...prev,
          [editDrinkForm.name]: editDrinkForm.image
        }));
      }

      toast.success(isSpanish ? 'Bebida actualizada' : 'Drink updated');
      setShowEditDrinkDialog(false);
      setEditingDrinkItem(null);
    } catch (error) {
      toast.error(isSpanish ? 'Error al guardar' : 'Failed to save');
    } finally {
      setSavingDrink(false);
    }
  };

  // Handle image upload in edit dialog
  const handleEditDrinkImageUpload = async (file) => {
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('drink_name', editDrinkForm.name || editingDrinkItem?.name || 'drink');
      
      const res = await api.post(`/restaurants/${selectedRestaurant.id}/drink-images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data?.image_url) {
        setEditDrinkForm(prev => ({ ...prev, image: res.data.image_url }));
        toast.success(isSpanish ? 'Imagen subida' : 'Image uploaded');
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error al subir imagen' : 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Get drink with any custom edits applied
  const getDrinkWithEdits = (drink) => {
    const customItem = customCatalogItems[drink.name];
    if (customItem) {
      return {
        ...drink,
        name: customItem.name || drink.name,
        category: customItem.category || drink.category,
        sizes: customItem.sizes || drink.sizes,
        image: customItem.image || drink.image
      };
    }
    return drink;
  };

  // Handle drink image upload
  const handleDrinkImageUpload = async (file) => {
    if (!file || !editingDrink) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('drink_name', editingDrink.name);
      
      const res = await api.post(`/restaurants/${selectedRestaurant.id}/drink-images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data?.image_url) {
        setCustomDrinkImages(prev => ({
          ...prev,
          [editingDrink.name]: res.data.image_url
        }));
        toast.success(isSpanish ? 'Imagen actualizada' : 'Image updated');
        setShowImageDialog(false);
        setEditingDrink(null);
        setDrinkImageUrl('');
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error al subir imagen' : 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Save drink image URL
  const handleSaveDrinkImageUrl = async () => {
    if (!drinkImageUrl || !editingDrink) return;
    
    setUploadingImage(true);
    try {
      await api.post(`/restaurants/${selectedRestaurant.id}/drink-images`, {
        drink_name: editingDrink.name,
        image_url: drinkImageUrl
      });
      
      setCustomDrinkImages(prev => ({
        ...prev,
        [editingDrink.name]: drinkImageUrl
      }));
      toast.success(isSpanish ? 'Imagen actualizada' : 'Image updated');
      setShowImageDialog(false);
      setEditingDrink(null);
      setDrinkImageUrl('');
    } catch (error) {
      toast.error(isSpanish ? 'Error al guardar imagen' : 'Failed to save image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Get drink icon based on category
  const getDrinkIcon = (drink, category) => {
    const name = drink.name.toLowerCase();
    const cat = (drink.category || category || '').toLowerCase();
    
    if (cat.includes('cerveza') || cat.includes('beer')) return Beer;
    if (cat.includes('vino') || cat.includes('wine') || cat.includes('cava') || cat.includes('champagne')) return Wine;
    if (cat.includes('café') || cat.includes('coffee') || cat.includes('té') || cat.includes('tea') || cat.includes('infusion')) return Coffee;
    if (cat.includes('cocktail') || cat.includes('cóctel') || cat.includes('gin') || cat.includes('vodka') || cat.includes('whisky') || cat.includes('ron') || cat.includes('rum')) return Martini;
    if (name.includes('agua') || name.includes('water') || cat.includes('agua')) return GlassWater;
    
    return GlassWater;
  };

  // Get drink image or fallback
  const getDrinkImage = (drink, categoryKey) => {
    // First check custom uploaded images
    if (customDrinkImages[drink.name]) {
      return customDrinkImages[drink.name];
    }
    // Then check drink's built-in image
    if (drink.image) {
      return drink.image;
    }
    return null;
  };

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/restaurants/${selectedRestaurant.id}/inventory`);
      setInventory(response.data);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      toast.error(isSpanish ? 'Error al cargar el inventario' : 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchAiCredits = async () => {
    try {
      const response = await api.get(`/restaurants/${selectedRestaurant.id}/ai-credits`);
      setAiCredits(response.data);
    } catch (error) {
      // Default values if endpoint doesn't exist yet
      setAiCredits({ used: 0, limit: 2, purchased: 0 });
    }
  };

  const fetchTaxRates = async () => {
    try {
      const response = await api.get(`/restaurants/${selectedRestaurant.id}/tax-rates`);
      setTaxRates(response.data);
    } catch (error) {
      // Default tax rates if none set
      setTaxRates([]);
    }
  };

  // Get unique categories
  const categories = [...new Set(inventory.map(item => item.category))].filter(Boolean);

  // Filter items
  const filteredItems = inventory.filter(item => {
    const matchesSearch = searchQuery === '' || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || item.stock_status === statusFilter;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Stats
  const stats = {
    total: inventory.length,
    outOfStock: inventory.filter(i => i.stock_status === 'out_of_stock').length,
    lowStock: inventory.filter(i => i.stock_status === 'low_stock').length,
    inStock: inventory.filter(i => i.stock_status === 'in_stock').length,
    unlimited: inventory.filter(i => i.stock_status === 'unlimited').length,
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditValue(item.stock_count?.toString() || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleSaveStock = async (itemId) => {
    try {
      setSaving(true);
      const stockCount = editValue === '' ? null : parseInt(editValue, 10);
      
      await api.put(`/menu/${itemId}/stock`, {
        stock_count: stockCount
      });
      
      toast.success(isSpanish ? 'Stock actualizado' : 'Stock updated');
      setEditingId(null);
      setEditValue('');
      fetchInventory();
    } catch (error) {
      console.error('Failed to update stock:', error);
      toast.error(isSpanish ? 'Error al actualizar' : 'Failed to update stock');
    } finally {
      setSaving(false);
    }
  };

  const handleSetUnlimited = async (itemId) => {
    try {
      await api.put(`/menu/${itemId}/stock`, { stock_count: null });
      toast.success(isSpanish ? 'Establecido como ilimitado' : 'Set to unlimited');
      fetchInventory();
    } catch (error) {
      toast.error(isSpanish ? 'Error al actualizar' : 'Failed to update');
    }
  };

  const handleDeleteItem = async (itemId, itemName) => {
    if (!window.confirm(isSpanish 
      ? `¿Estás seguro de eliminar "${itemName}"? Esta acción no se puede deshacer.`
      : `Are you sure you want to delete "${itemName}"? This action cannot be undone.`
    )) return;
    
    try {
      await api.delete(`/menu/${itemId}`);
      toast.success(isSpanish ? 'Item eliminado' : 'Item deleted');
      fetchInventory();
    } catch (error) {
      toast.error(isSpanish ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'out_of_stock':
        return <Badge className="bg-red-500 text-white">{isSpanish ? 'Agotado' : 'Out of Stock'}</Badge>;
      case 'low_stock':
        return <Badge className="bg-amber-500 text-white">{isSpanish ? 'Stock Bajo' : 'Low Stock'}</Badge>;
      case 'in_stock':
        return <Badge className="bg-green-500 text-white">{isSpanish ? 'En Stock' : 'In Stock'}</Badge>;
      case 'unlimited':
        return <Badge variant="outline" className="text-slate-500">{isSpanish ? 'Ilimitado' : 'Unlimited'}</Badge>;
      default:
        return <Badge variant="outline">{isSpanish ? 'Desconocido' : 'Unknown'}</Badge>;
    }
  };

  // AI Receipt Scanning
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if user has credits
      const totalCredits = aiCredits.limit + aiCredits.purchased - aiCredits.used;
      if (totalCredits <= 0) {
        setShowCreditsDialog(true);
        return;
      }
      
      setScanImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScanPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setShowScanDialog(true);
    }
  };

  const handleScanReceipt = async () => {
    if (!scanImage) return;
    
    try {
      setScanning(true);
      
      // Convert to base64
      const reader = new FileReader();
      reader.readAsDataURL(scanImage);
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        
        try {
          const response = await api.post(`/restaurants/${selectedRestaurant.id}/stock/scan-receipt`, {
            image_base64: base64
          });
          
          setScanResults(response.data);
          fetchAiCredits(); // Refresh credits
        } catch (error) {
          if (error.response?.status === 403) {
            setShowCreditsDialog(true);
            setShowScanDialog(false);
          } else {
            toast.error(isSpanish ? 'Error al escanear recibo' : 'Failed to scan receipt');
          }
        } finally {
          setScanning(false);
        }
      };
    } catch (error) {
      setScanning(false);
      toast.error(isSpanish ? 'Error al procesar imagen' : 'Failed to process image');
    }
  };

  const handleMergeStock = async () => {
    if (!scanResults?.items) return;
    
    try {
      setSaving(true);
      await api.post(`/restaurants/${selectedRestaurant.id}/stock/merge`, {
        items: scanResults.items
      });
      
      toast.success(isSpanish ? 'Stock actualizado desde recibo' : 'Stock updated from receipt');
      setShowScanDialog(false);
      setScanResults(null);
      setScanImage(null);
      setScanPreview(null);
      fetchInventory();
    } catch (error) {
      toast.error(isSpanish ? 'Error al fusionar stock' : 'Failed to merge stock');
    } finally {
      setSaving(false);
    }
  };

  // Drinks Catalog
  const drinksCatalog = {
    soft: {
      label: isSpanish ? 'Refrescos' : 'Soft Drinks',
      icon: GlassWater,
      items: [
        // Coca-Cola family
        { name: 'Coca-Cola', sizes: ['33cl Lata', '50cl Botella', '1L Botella', '1.5L Botella', '2L Botella'], category: 'Refrescos', image: 'https://images.unsplash.com/photo-1554866585-cd94860890b7?w=100&h=100&fit=crop' },
        { name: 'Coca-Cola Zero', sizes: ['33cl Lata', '50cl Botella', '1L Botella', '1.5L Botella', '2L Botella'], category: 'Refrescos', image: 'https://images.unsplash.com/photo-1629203851122-3726ecdf080e?w=100&h=100&fit=crop' },
        { name: 'Coca-Cola Light', sizes: ['33cl Lata', '50cl Botella'], category: 'Refrescos' },
        { name: 'Fanta Naranja', sizes: ['33cl Lata', '50cl Botella', '1L Botella', '2L Botella'], category: 'Refrescos', image: 'https://images.unsplash.com/photo-1624517452488-04869289c4ca?w=100&h=100&fit=crop' },
        { name: 'Fanta Limón', sizes: ['33cl Lata', '50cl Botella', '1L Botella', '2L Botella'], category: 'Refrescos' },
        { name: 'Sprite', sizes: ['33cl Lata', '50cl Botella', '1L Botella', '2L Botella'], category: 'Refrescos' },
        { name: 'Kas Naranja', sizes: ['33cl Lata', '50cl Botella', '1L Botella', '2L Botella'], category: 'Refrescos' },
        { name: 'Kas Limón', sizes: ['33cl Lata', '50cl Botella', '1L Botella', '2L Botella'], category: 'Refrescos' },
        { name: 'Pepsi', sizes: ['33cl Lata', '50cl Botella', '1L Botella', '2L Botella'], category: 'Refrescos' },
        { name: 'Pepsi Max', sizes: ['33cl Lata', '50cl Botella'], category: 'Refrescos' },
        { name: '7 Up', sizes: ['33cl Lata', '50cl Botella', '1L Botella', '2L Botella'], category: 'Refrescos' },
        { name: 'Schweppes Tónica', sizes: ['20cl Botella', '33cl Lata', '1L Botella'], category: 'Refrescos/Mixers' },
        { name: 'Schweppes Limón', sizes: ['20cl Botella', '33cl Lata'], category: 'Refrescos/Mixers' },
        { name: 'Schweppes Naranja', sizes: ['20cl Botella', '33cl Lata'], category: 'Refrescos/Mixers' },
        { name: 'Aquarius Limón', sizes: ['33cl Lata', '50cl Botella', '1.5L Botella'], category: 'Refrescos' },
        { name: 'Aquarius Naranja', sizes: ['33cl Lata', '50cl Botella', '1.5L Botella'], category: 'Refrescos' },
        { name: 'Nestea Limón', sizes: ['33cl Lata', '50cl Botella', '1.5L Botella'], category: 'Refrescos' },
        { name: 'Nestea Melocotón', sizes: ['33cl Lata', '50cl Botella', '1.5L Botella'], category: 'Refrescos' },
        { name: 'Red Bull', sizes: ['25cl Lata', '35.5cl Lata'], category: 'Energéticas' },
        { name: 'Monster Energy', sizes: ['50cl Lata'], category: 'Energéticas' },
        // Water
        { name: 'Agua Mineral', sizes: ['33cl Botella', '50cl Botella', '1L Botella', '1.5L Botella', '5L Garrafa'], category: 'Aguas' },
        { name: 'Agua con Gas', sizes: ['33cl Botella', '50cl Botella', '1L Botella'], category: 'Aguas' },
        { name: 'Bezoya', sizes: ['33cl', '50cl', '1L', '1.5L'], category: 'Aguas' },
        { name: 'Font Vella', sizes: ['33cl', '50cl', '1L', '1.5L'], category: 'Aguas' },
        { name: 'Lanjarón', sizes: ['33cl', '50cl', '1L', '1.5L'], category: 'Aguas' },
        { name: 'Solan de Cabras', sizes: ['33cl', '50cl', '1L', '1.5L'], category: 'Aguas' },
        // Juices
        { name: 'Zumo de Naranja Natural', sizes: ['Vaso', 'Jarra'], category: 'Zumos' },
        { name: 'Zumo de Naranja (Botella)', sizes: ['20cl', '33cl', '1L'], category: 'Zumos' },
        { name: 'Zumo de Piña', sizes: ['20cl', '33cl', '1L'], category: 'Zumos' },
        { name: 'Zumo de Melocotón', sizes: ['20cl', '33cl', '1L'], category: 'Zumos' },
        { name: 'Zumo de Manzana', sizes: ['20cl', '33cl', '1L'], category: 'Zumos' },
        { name: 'Zumo de Tomate', sizes: ['20cl', '33cl'], category: 'Zumos' },
      ]
    },
    beer: {
      label: isSpanish ? 'Cervezas' : 'Beers',
      icon: Beer,
      items: [
        // Spanish beers
        { name: 'Estrella Galicia', sizes: ['Caña', 'Tubo', 'Jarra', '33cl Botellín', '33cl Lata', '1L Botella'], category: 'Cervezas', image: 'https://images.unsplash.com/photo-1608270586620-248524c67de9?w=100&h=100&fit=crop' },
        { name: 'Mahou Clásica', sizes: ['Caña', 'Tubo', 'Jarra', '33cl Botellín', '33cl Lata', '1L Botella'], category: 'Cervezas' },
        { name: 'Mahou 5 Estrellas', sizes: ['Caña', 'Tubo', 'Jarra', '33cl Botellín', '33cl Lata'], category: 'Cervezas' },
        { name: 'Mahou Sin', sizes: ['Caña', '33cl Botellín', '33cl Lata'], category: 'Cervezas Sin Alcohol' },
        { name: 'San Miguel Especial', sizes: ['Caña', 'Tubo', 'Jarra', '33cl Botellín', '33cl Lata'], category: 'Cervezas' },
        { name: 'San Miguel 0,0', sizes: ['Caña', '33cl Botellín', '33cl Lata'], category: 'Cervezas Sin Alcohol' },
        { name: 'Cruzcampo', sizes: ['Caña', 'Tubo', 'Jarra', '33cl Botellín', '33cl Lata', '1L Botella'], category: 'Cervezas' },
        { name: 'Cruzcampo Sin', sizes: ['Caña', '33cl Botellín', '33cl Lata'], category: 'Cervezas Sin Alcohol' },
        { name: 'Alhambra Reserva 1925', sizes: ['33cl Botellín', 'Caña'], category: 'Cervezas Premium' },
        { name: 'Alhambra Especial', sizes: ['Caña', 'Tubo', '33cl Botellín', '33cl Lata'], category: 'Cervezas' },
        { name: 'Victoria', sizes: ['Caña', 'Tubo', '33cl Botellín', '33cl Lata'], category: 'Cervezas' },
        { name: 'Voll-Damm', sizes: ['Caña', '33cl Botellín', '33cl Lata'], category: 'Cervezas Premium' },
        { name: 'Estrella Damm', sizes: ['Caña', 'Tubo', 'Jarra', '33cl Botellín', '33cl Lata'], category: 'Cervezas' },
        { name: 'Moritz', sizes: ['Caña', '33cl Botellín'], category: 'Cervezas' },
        { name: 'Amstel', sizes: ['Caña', 'Tubo', '33cl Botellín', '33cl Lata'], category: 'Cervezas' },
        // International beers
        { name: 'Heineken', sizes: ['Caña', '33cl Botellín', '33cl Lata'], category: 'Cervezas Importadas' },
        { name: 'Corona', sizes: ['35.5cl Botella'], category: 'Cervezas Importadas' },
        { name: 'Budweiser', sizes: ['33cl Botellín', '33cl Lata'], category: 'Cervezas Importadas' },
        { name: 'Guinness', sizes: ['Pinta', '44cl Lata'], category: 'Cervezas Importadas' },
        { name: 'Desperados', sizes: ['33cl Botellín'], category: 'Cervezas Especiales' },
        // Craft beers placeholder
        { name: 'Cerveza Artesana IPA', sizes: ['33cl Botella'], category: 'Cervezas Artesanas' },
        { name: 'Cerveza Artesana Lager', sizes: ['33cl Botella'], category: 'Cervezas Artesanas' },
        { name: 'Radler/Clara', sizes: ['Caña', '33cl Botellín', '33cl Lata'], category: 'Cervezas' },
      ]
    },
    wine: {
      label: isSpanish ? 'Vinos' : 'Wines',
      icon: Wine,
      items: [
        // Red wines by region
        { name: 'Rioja Crianza', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Tintos' },
        { name: 'Rioja Reserva', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Tintos' },
        { name: 'Rioja Gran Reserva', sizes: ['Botella 75cl'], category: 'Vinos Tintos' },
        { name: 'Ribera del Duero Crianza', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Tintos' },
        { name: 'Ribera del Duero Reserva', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Tintos' },
        { name: 'Toro', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Tintos' },
        { name: 'Jumilla', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Tintos' },
        { name: 'Priorat', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Tintos' },
        { name: 'Somontano Tinto', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Tintos' },
        { name: 'Valdepeñas Tinto', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Tintos' },
        { name: 'Vino Tinto de la Casa', sizes: ['Copa', 'Jarra', 'Botella 75cl'], category: 'Vinos Tintos' },
        // White wines
        { name: 'Albariño (Rías Baixas)', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Blancos' },
        { name: 'Verdejo (Rueda)', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Blancos' },
        { name: 'Godello', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Blancos' },
        { name: 'Txakoli', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Blancos' },
        { name: 'Rioja Blanco', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Blancos' },
        { name: 'Penedès Blanco', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Blancos' },
        { name: 'Vino Blanco de la Casa', sizes: ['Copa', 'Jarra', 'Botella 75cl'], category: 'Vinos Blancos' },
        // Rosé
        { name: 'Rosado de Navarra', sizes: ['Copa', 'Botella 75cl'], category: 'Vinos Rosados' },
        { name: 'Rosado de la Casa', sizes: ['Copa', 'Jarra', 'Botella 75cl'], category: 'Vinos Rosados' },
        // Sparkling
        { name: 'Cava Brut', sizes: ['Copa', 'Botella 75cl'], category: 'Cavas y Espumosos' },
        { name: 'Cava Brut Nature', sizes: ['Copa', 'Botella 75cl'], category: 'Cavas y Espumosos' },
        { name: 'Cava Semiseco', sizes: ['Copa', 'Botella 75cl'], category: 'Cavas y Espumosos' },
        { name: 'Cava Rosado', sizes: ['Copa', 'Botella 75cl'], category: 'Cavas y Espumosos' },
        { name: 'Champagne', sizes: ['Copa', 'Botella 75cl'], category: 'Cavas y Espumosos' },
        { name: 'Prosecco', sizes: ['Copa', 'Botella 75cl'], category: 'Cavas y Espumosos' },
        // Fortified
        { name: 'Jerez Fino', sizes: ['Copa', 'Botella'], category: 'Vinos Generosos' },
        { name: 'Jerez Manzanilla', sizes: ['Copa', 'Botella'], category: 'Vinos Generosos' },
        { name: 'Jerez Amontillado', sizes: ['Copa', 'Botella'], category: 'Vinos Generosos' },
        { name: 'Jerez Oloroso', sizes: ['Copa', 'Botella'], category: 'Vinos Generosos' },
        { name: 'Pedro Ximénez', sizes: ['Copa', 'Botella'], category: 'Vinos Generosos' },
        { name: 'Moscatel', sizes: ['Copa', 'Botella'], category: 'Vinos Generosos' },
      ]
    },
    spirits: {
      label: isSpanish ? 'Licores y Destilados' : 'Spirits & Liquors',
      icon: Wine,
      items: [
        // Gin - stock: bottles, menu: serving sizes
        { name: 'Beefeater', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Gin' },
        { name: 'Tanqueray', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Gin' },
        { name: 'Bombay Sapphire', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Gin' },
        { name: 'Hendricks', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Gin Premium' },
        { name: 'Puerto de Indias', stockSizes: ['Botella 70cl'], menuSizes: ['Copa'], category: 'Gin' },
        { name: 'Larios', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Gin' },
        // Vodka
        { name: 'Absolut', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Vodka' },
        { name: 'Smirnoff', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Vodka' },
        { name: 'Grey Goose', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Vodka Premium' },
        // Rum
        { name: 'Bacardí Blanco', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Ron' },
        { name: 'Bacardí Oro', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Ron' },
        { name: 'Havana Club 3 Años', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Ron' },
        { name: 'Havana Club 7 Años', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Ron' },
        { name: 'Captain Morgan', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Ron' },
        { name: 'Brugal', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Ron' },
        // Whisky
        { name: 'JB', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Whisky' },
        { name: 'Ballantines', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Whisky' },
        { name: 'Johnnie Walker Red', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Whisky' },
        { name: 'Johnnie Walker Black', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Whisky Premium' },
        { name: 'Jack Daniels', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Whisky Americano' },
        { name: 'Jameson', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Whisky Irlandés' },
        { name: 'Chivas Regal 12', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Whisky Premium' },
        // Spanish brandy
        { name: 'Veterano', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Brandy' },
        { name: 'Soberano', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Brandy' },
        { name: 'Carlos I', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Brandy Premium' },
        { name: 'Torres 10', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Brandy' },
        // Traditional Spanish
        { name: 'Vermut Rojo', stockSizes: ['Botella 1L', 'Grifo 5L'], menuSizes: ['Copa', 'Vaso'], category: 'Vermut' },
        { name: 'Vermut Blanco', stockSizes: ['Botella 1L', 'Grifo 5L'], menuSizes: ['Copa', 'Vaso'], category: 'Vermut' },
        { name: 'Orujo Blanco', stockSizes: ['Botella 70cl'], menuSizes: ['Chupito', 'Copa'], category: 'Licores Españoles' },
        { name: 'Orujo de Hierbas', stockSizes: ['Botella 70cl'], menuSizes: ['Chupito', 'Copa'], category: 'Licores Españoles' },
        { name: 'Orujo de Café', stockSizes: ['Botella 70cl'], menuSizes: ['Chupito', 'Copa'], category: 'Licores Españoles' },
        { name: 'Pacharán', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Chupito', 'Copa'], category: 'Licores Españoles' },
        { name: 'Licor de Hierbas', stockSizes: ['Botella 70cl'], menuSizes: ['Chupito', 'Copa'], category: 'Licores Españoles' },
        { name: 'Aguardiente', stockSizes: ['Botella 70cl'], menuSizes: ['Chupito'], category: 'Licores Españoles' },
        // Liqueurs
        { name: 'Baileys', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Licores' },
        { name: 'Amaretto', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Licores' },
        { name: 'Kahlúa', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Licores' },
        { name: 'Jägermeister', stockSizes: ['Botella 70cl', 'Botella 1L'], menuSizes: ['Copa', 'Chupito'], category: 'Licores' },
        { name: 'Limoncello', stockSizes: ['Botella 70cl'], menuSizes: ['Chupito'], category: 'Licores' },
        { name: 'Licor 43', stockSizes: ['Botella 70cl'], menuSizes: ['Copa', 'Chupito'], category: 'Licores' },
        // Tequila
        { name: 'Tequila Blanco', stockSizes: ['Botella 70cl'], menuSizes: ['Chupito', 'Copa'], category: 'Tequila' },
        { name: 'Tequila Reposado', stockSizes: ['Botella 70cl'], menuSizes: ['Chupito', 'Copa'], category: 'Tequila' },
      ]
    },
    cocktails: {
      label: isSpanish ? 'Cócteles y Combinados' : 'Cocktails & Mixed Drinks',
      icon: Wine,
      items: [
        // === COMBINADOS (Most popular in Spanish bars) ===
        // Gin Tonics
        { name: 'Gin Tonic Clásico', menuSizes: ['Copa'], category: 'Combinados/Gin Tonic', ingredients: 'Gin + Tónica + Limón' },
        { name: 'Gin Tonic Beefeater', menuSizes: ['Copa'], category: 'Combinados/Gin Tonic', ingredients: 'Beefeater + Tónica + Limón' },
        { name: 'Gin Tonic Tanqueray', menuSizes: ['Copa'], category: 'Combinados/Gin Tonic', ingredients: 'Tanqueray + Tónica + Pepino' },
        { name: 'Gin Tonic Bombay', menuSizes: ['Copa'], category: 'Combinados/Gin Tonic', ingredients: 'Bombay Sapphire + Tónica + Limón' },
        { name: 'Gin Tonic Hendricks', menuSizes: ['Copa'], category: 'Combinados/Gin Tonic Premium', ingredients: 'Hendricks + Tónica Fever Tree + Pepino' },
        { name: 'Gin Tonic Puerto de Indias', menuSizes: ['Copa'], category: 'Combinados/Gin Tonic', ingredients: 'Puerto de Indias + Tónica + Fresas' },
        { name: 'Gin Tonic Larios', menuSizes: ['Copa'], category: 'Combinados/Gin Tonic', ingredients: 'Larios + Tónica + Limón' },
        // Vodka combinados
        { name: 'Vodka con Naranja', menuSizes: ['Copa', 'Tubo'], category: 'Combinados/Vodka', ingredients: 'Vodka + Zumo de Naranja' },
        { name: 'Vodka con Limón', menuSizes: ['Copa', 'Tubo'], category: 'Combinados/Vodka', ingredients: 'Vodka + Fanta Limón' },
        { name: 'Vodka con Red Bull', menuSizes: ['Copa'], category: 'Combinados/Vodka', ingredients: 'Vodka + Red Bull' },
        { name: 'Destornillador', menuSizes: ['Copa'], category: 'Combinados/Vodka', ingredients: 'Vodka + Zumo de Naranja Natural' },
        // Ron combinados
        { name: 'Ron con Cola (Cuba Libre)', menuSizes: ['Copa', 'Tubo'], category: 'Combinados/Ron', ingredients: 'Ron + Coca Cola + Lima' },
        { name: 'Ron con Naranja', menuSizes: ['Copa', 'Tubo'], category: 'Combinados/Ron', ingredients: 'Ron + Fanta Naranja' },
        { name: 'Ron con Limón', menuSizes: ['Copa', 'Tubo'], category: 'Combinados/Ron', ingredients: 'Ron + Fanta Limón' },
        // Whisky combinados
        { name: 'Whisky con Cola', menuSizes: ['Copa', 'Tubo'], category: 'Combinados/Whisky', ingredients: 'Whisky + Coca Cola' },
        { name: 'Whisky con Naranja', menuSizes: ['Copa', 'Tubo'], category: 'Combinados/Whisky', ingredients: 'Whisky + Fanta Naranja' },
        { name: 'Whisky con Ginger Ale', menuSizes: ['Copa'], category: 'Combinados/Whisky', ingredients: 'Whisky + Ginger Ale' },
        { name: 'Jack & Coke', menuSizes: ['Copa'], category: 'Combinados/Whisky', ingredients: 'Jack Daniels + Coca Cola' },
        // Brandy combinados
        { name: 'Brandy con Cola', menuSizes: ['Copa', 'Tubo'], category: 'Combinados/Brandy', ingredients: 'Brandy + Coca Cola' },
        
        // === CÓCTELES CLÁSICOS ===
        { name: 'Mojito', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Ron Blanco + Menta + Lima + Azúcar + Soda' },
        { name: 'Mojito de Fresa', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Ron Blanco + Menta + Fresas + Lima + Azúcar' },
        { name: 'Mojito de Maracuyá', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Ron Blanco + Menta + Maracuyá + Lima' },
        { name: 'Caipirinha', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Cachaça + Lima + Azúcar' },
        { name: 'Caipiroska', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Vodka + Lima + Azúcar' },
        { name: 'Piña Colada', menuSizes: ['Copa'], category: 'Cócteles Tropicales', ingredients: 'Ron + Leche de Coco + Piña' },
        { name: 'Daiquiri', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Ron Blanco + Lima + Azúcar' },
        { name: 'Daiquiri de Fresa', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Ron + Fresas + Lima + Azúcar' },
        { name: 'Margarita', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Tequila + Triple Sec + Lima + Sal' },
        { name: 'Margarita de Fresa', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Tequila + Triple Sec + Fresas + Lima' },
        { name: 'Cosmopolitan', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Vodka + Triple Sec + Arándanos + Lima' },
        { name: 'Sex on the Beach', menuSizes: ['Copa'], category: 'Cócteles Tropicales', ingredients: 'Vodka + Melocotón + Naranja + Arándanos' },
        { name: 'Long Island Iced Tea', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Vodka + Gin + Ron + Tequila + Triple Sec + Cola' },
        { name: 'Tequila Sunrise', menuSizes: ['Copa'], category: 'Cócteles Tropicales', ingredients: 'Tequila + Naranja + Granadina' },
        
        // === APERITIVOS ===
        { name: 'Aperol Spritz', menuSizes: ['Copa'], category: 'Aperitivos', ingredients: 'Aperol + Prosecco + Soda + Naranja' },
        { name: 'Hugo Spritz', menuSizes: ['Copa'], category: 'Aperitivos', ingredients: 'Flor de Saúco + Prosecco + Menta' },
        { name: 'Americano', menuSizes: ['Copa'], category: 'Aperitivos', ingredients: 'Campari + Vermut Rojo + Soda' },
        { name: 'Negroni', menuSizes: ['Copa'], category: 'Aperitivos', ingredients: 'Gin + Campari + Vermut Rojo' },
        
        // === CLÁSICOS DE BAR ===
        { name: 'Manhattan', menuSizes: ['Copa'], category: 'Cócteles Premium', ingredients: 'Whisky Rye + Vermut Rojo + Angostura' },
        { name: 'Old Fashioned', menuSizes: ['Copa'], category: 'Cócteles Premium', ingredients: 'Bourbon + Azúcar + Angostura + Naranja' },
        { name: 'Whisky Sour', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Whisky + Limón + Azúcar + Clara de Huevo' },
        { name: 'Bloody Mary', menuSizes: ['Copa'], category: 'Cócteles Clásicos', ingredients: 'Vodka + Tomate + Limón + Tabasco + Worcestershire' },
        { name: 'Mimosa', menuSizes: ['Copa'], category: 'Cócteles con Cava', ingredients: 'Cava + Zumo de Naranja' },
        { name: 'Bellini', menuSizes: ['Copa'], category: 'Cócteles con Cava', ingredients: 'Prosecco + Melocotón' },
        { name: 'Kir Royal', menuSizes: ['Copa'], category: 'Cócteles con Cava', ingredients: 'Cava + Licor de Cassis' },
        
        // === CÓCTELES ESPAÑOLES ===
        { name: 'Sangría', menuSizes: ['Vaso', 'Jarra 1L', 'Jarra 1.5L'], category: 'Cócteles Españoles', ingredients: 'Vino Tinto + Frutas + Azúcar + Licor' },
        { name: 'Sangría Blanca', menuSizes: ['Vaso', 'Jarra 1L'], category: 'Cócteles Españoles', ingredients: 'Vino Blanco + Frutas + Azúcar' },
        { name: 'Tinto de Verano', menuSizes: ['Vaso', 'Jarra'], category: 'Cócteles Españoles', ingredients: 'Vino Tinto + Gaseosa/Casera + Limón' },
        { name: 'Kalimotxo', menuSizes: ['Vaso'], category: 'Cócteles Españoles', ingredients: 'Vino Tinto + Coca Cola' },
        { name: 'Rebujito', menuSizes: ['Vaso', 'Jarra'], category: 'Cócteles Españoles', ingredients: 'Manzanilla + 7Up + Hierbabuena' },
        { name: 'Agua de Valencia', menuSizes: ['Copa', 'Jarra'], category: 'Cócteles Españoles', ingredients: 'Cava + Naranja + Vodka + Gin' },
        { name: 'Zurracapote', menuSizes: ['Vaso'], category: 'Cócteles Españoles', ingredients: 'Vino Tinto + Frutas + Canela + Azúcar' },
        
        // === CHUPITOS POPULARES ===
        { name: 'Chupito de Tequila', menuSizes: ['Chupito'], category: 'Chupitos', ingredients: 'Tequila + Sal + Limón' },
        { name: 'Chupito de Jäger', menuSizes: ['Chupito'], category: 'Chupitos', ingredients: 'Jägermeister' },
        { name: 'B-52', menuSizes: ['Chupito'], category: 'Chupitos', ingredients: 'Kahlúa + Baileys + Grand Marnier' },
        { name: 'Chupito de Pacharán', menuSizes: ['Chupito'], category: 'Chupitos', ingredients: 'Pacharán' },
        { name: 'Orgasmo', menuSizes: ['Chupito'], category: 'Chupitos', ingredients: 'Baileys + Amaretto + Kahlúa' },
        { name: 'Kamikaze', menuSizes: ['Chupito'], category: 'Chupitos', ingredients: 'Vodka + Triple Sec + Lima' },
        
        // === SIN ALCOHOL ===
        { name: 'Virgin Mojito', menuSizes: ['Copa'], category: 'Sin Alcohol', ingredients: 'Menta + Lima + Azúcar + Soda' },
        { name: 'Shirley Temple', menuSizes: ['Copa'], category: 'Sin Alcohol', ingredients: 'Ginger Ale + Granadina + Cereza' },
        { name: 'Mocktail de Frutas', menuSizes: ['Copa'], category: 'Sin Alcohol', ingredients: 'Frutas de temporada + Zumos + Soda' },
        { name: 'San Francisco', menuSizes: ['Copa'], category: 'Sin Alcohol', ingredients: 'Naranja + Melocotón + Piña + Granadina' },
        { name: 'Virgin Piña Colada', menuSizes: ['Copa'], category: 'Sin Alcohol', ingredients: 'Leche de Coco + Piña + Nata' },
      ]
    },
    coffee: {
      label: isSpanish ? 'Cafés e Infusiones' : 'Coffee & Hot Drinks',
      icon: Coffee,
      items: [
        // Coffee
        { name: 'Café Solo', sizes: ['Normal', 'Doble'], category: 'Cafés' },
        { name: 'Café Cortado', sizes: ['Normal'], category: 'Cafés' },
        { name: 'Café con Leche', sizes: ['Normal', 'Grande'], category: 'Cafés' },
        { name: 'Café Americano', sizes: ['Normal', 'Grande'], category: 'Cafés' },
        { name: 'Café con Hielo', sizes: ['Normal'], category: 'Cafés' },
        { name: 'Carajillo', sizes: ['Normal'], category: 'Cafés con Alcohol' },
        { name: 'Café Bombón', sizes: ['Normal'], category: 'Cafés' },
        { name: 'Cappuccino', sizes: ['Normal', 'Grande'], category: 'Cafés' },
        { name: 'Latte', sizes: ['Normal', 'Grande'], category: 'Cafés' },
        { name: 'Espresso', sizes: ['Simple', 'Doble'], category: 'Cafés' },
        { name: 'Café Descafeinado', sizes: ['Solo', 'Con Leche'], category: 'Cafés' },
        { name: 'Café Irlandés', sizes: ['Copa'], category: 'Cafés con Alcohol' },
        // Tea & infusions
        { name: 'Té Negro', sizes: ['Taza'], category: 'Tés e Infusiones' },
        { name: 'Té Verde', sizes: ['Taza'], category: 'Tés e Infusiones' },
        { name: 'Té con Limón', sizes: ['Taza'], category: 'Tés e Infusiones' },
        { name: 'Manzanilla', sizes: ['Taza'], category: 'Tés e Infusiones' },
        { name: 'Poleo Menta', sizes: ['Taza'], category: 'Tés e Infusiones' },
        { name: 'Tila', sizes: ['Taza'], category: 'Tés e Infusiones' },
        { name: 'Infusión de Frutas', sizes: ['Taza'], category: 'Tés e Infusiones' },
        // Hot chocolate
        { name: 'Chocolate Caliente', sizes: ['Taza'], category: 'Chocolate' },
        { name: 'Chocolate con Churros', sizes: ['Ración'], category: 'Chocolate' },
        { name: 'Cola Cao', sizes: ['Vaso'], category: 'Chocolate' },
      ]
    }
  };

  const handleDrinkToggle = (drink, size) => {
    const key = `${drink.name}-${size}`;
    setSelectedDrinks(prev => {
      const exists = prev.find(d => d.key === key);
      if (exists) {
        return prev.filter(d => d.key !== key);
      }
      return [...prev, { key, name: `${drink.name} (${size})`, category: drink.category, image: drink.image }];
    });
  };

  const handleAddDrinksToMenu = async () => {
    if (selectedDrinks.length === 0) return;
    
    try {
      setAddingDrinks(true);
      await api.post(`/restaurants/${selectedRestaurant.id}/menu/add-drinks`, {
        drinks: selectedDrinks.map(d => ({
          name: d.name,
          category: d.category,
          image_url: d.image || null
        }))
      });
      
      toast.success(isSpanish 
        ? `${selectedDrinks.length} bebidas añadidas al menú` 
        : `${selectedDrinks.length} drinks added to menu`);
      setShowDrinksDialog(false);
      setSelectedDrinks([]);
      fetchInventory();
    } catch (error) {
      toast.error(isSpanish ? 'Error al añadir bebidas' : 'Failed to add drinks');
    } finally {
      setAddingDrinks(false);
    }
  };

  if (!selectedRestaurant) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-slate-500">{isSpanish ? 'No hay restaurante seleccionado' : 'No restaurant selected'}</p>
      </div>
    );
  }

  const remainingCredits = aiCredits.limit + aiCredits.purchased - aiCredits.used;

  return (
    <div className="space-y-6" data-testid="stock-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isSpanish ? 'Gestión de Stock' : 'Stock Management'}
          </h1>
          <p className="text-slate-500">
            {isSpanish ? 'Rastrea y gestiona niveles de stock' : 'Track and manage stock levels'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowTaxDialog(true)} variant="outline">
            <Percent className="w-4 h-4 mr-2" />
            {isSpanish ? 'Impuestos' : 'Tax Rates'}
          </Button>
          <Button onClick={fetchInventory} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {isSpanish ? 'Actualizar' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => fileInputRef.current?.click()}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Camera className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{isSpanish ? 'Escanear Recibo' : 'Scan Receipt'}</h3>
                <p className="text-purple-100 text-sm">
                  {isSpanish ? 'AI añade stock automáticamente' : 'AI adds stock automatically'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Sparkles className="w-3 h-3" />
                  <span className="text-xs">{remainingCredits} {isSpanish ? 'escaneos restantes' : 'scans remaining'}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFileSelect}
        />

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setShowDrinksDialog(true)}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Wine className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{isSpanish ? 'Añadir Bebidas' : 'Add Drinks'}</h3>
                <p className="text-orange-100 text-sm">
                  {isSpanish ? 'Catálogo completo de bebidas' : 'Complete drinks catalog'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setShowCreditsDialog(true)}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <CreditCard className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{isSpanish ? 'Créditos AI' : 'AI Credits'}</h3>
                <p className="text-emerald-100 text-sm">
                  {remainingCredits} {isSpanish ? 'disponibles' : 'available'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Package className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-slate-500">{isSpanish ? 'Total Items' : 'Total Items'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={stats.outOfStock > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${stats.outOfStock > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                <PackageOpen className={`w-5 h-5 ${stats.outOfStock > 0 ? 'text-red-600' : 'text-slate-600'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.outOfStock}</p>
                <p className="text-xs text-slate-500">{isSpanish ? 'Agotado' : 'Out of Stock'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={stats.lowStock > 0 ? 'border-amber-200 bg-amber-50' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${stats.lowStock > 0 ? 'bg-amber-100' : 'bg-slate-100'}`}>
                <TrendingDown className={`w-5 h-5 ${stats.lowStock > 0 ? 'text-amber-600' : 'text-slate-600'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.lowStock}</p>
                <p className="text-xs text-slate-500">{isSpanish ? 'Stock Bajo' : 'Low Stock'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.inStock}</p>
                <p className="text-xs text-slate-500">{isSpanish ? 'En Stock' : 'In Stock'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Package className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.unlimited}</p>
                <p className="text-xs text-slate-500">{isSpanish ? 'Ilimitado' : 'Unlimited'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alerts */}
      {(stats.outOfStock > 0 || stats.lowStock > 0) && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-800 flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5" />
              {isSpanish ? 'Alertas de Stock' : 'Stock Alerts'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {inventory
                .filter(i => i.stock_status === 'out_of_stock' || i.stock_status === 'low_stock')
                .slice(0, 10)
                .map(item => (
                  <Badge 
                    key={item.id}
                    className={item.stock_status === 'out_of_stock' ? 'bg-red-500' : 'bg-amber-500'}
                  >
                    {item.name}: {item.stock_count ?? 0}
                  </Badge>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={isSpanish ? 'Buscar items...' : 'Search items...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="stock-search"
          />
        </div>
        
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]" data-testid="category-filter">
            <SelectValue placeholder={isSpanish ? 'Todas las Categorías' : 'All Categories'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isSpanish ? 'Todas las Categorías' : 'All Categories'}</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder={isSpanish ? 'Todos los Estados' : 'All Status'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isSpanish ? 'Todos los Estados' : 'All Status'}</SelectItem>
            <SelectItem value="out_of_stock">{isSpanish ? 'Agotado' : 'Out of Stock'}</SelectItem>
            <SelectItem value="low_stock">{isSpanish ? 'Stock Bajo' : 'Low Stock'}</SelectItem>
            <SelectItem value="in_stock">{isSpanish ? 'En Stock' : 'In Stock'}</SelectItem>
            <SelectItem value="unlimited">{isSpanish ? 'Ilimitado' : 'Unlimited'}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Inventory Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{isSpanish ? 'Nombre' : 'Item Name'}</TableHead>
                <TableHead>{isSpanish ? 'Categoría' : 'Category'}</TableHead>
                <TableHead>{isSpanish ? 'Precio' : 'Price'}</TableHead>
                <TableHead className="text-center">{isSpanish ? 'Stock' : 'Stock Count'}</TableHead>
                <TableHead className="text-center">{isSpanish ? 'Estado' : 'Status'}</TableHead>
                <TableHead className="text-right">{isSpanish ? 'Acciones' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    {isSpanish ? 'Cargando inventario...' : 'Loading inventory...'}
                  </TableCell>
                </TableRow>
              ) : filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    {isSpanish ? 'No se encontraron items' : 'No items found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map(item => (
                  <TableRow 
                    key={item.id}
                    className={item.stock_status === 'out_of_stock' ? 'bg-red-50' : 
                               item.stock_status === 'low_stock' ? 'bg-amber-50' : ''}
                    data-testid={`stock-item-${item.id}`}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        {item.image_url && (
                          <img 
                            src={item.image_url} 
                            alt={item.name}
                            className="w-10 h-10 rounded object-cover"
                          />
                        )}
                        <div>
                          <p>{item.name}</p>
                          {!item.is_available && (
                            <span className="text-xs text-red-500">{isSpanish ? 'No disponible' : 'Unavailable'}</span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>{formatPrice(item.price)}</TableCell>
                    <TableCell className="text-center">
                      {editingId === item.id ? (
                        <Input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="∞"
                          className="w-24 mx-auto text-center"
                          min="0"
                          autoFocus
                          data-testid={`stock-input-${item.id}`}
                        />
                      ) : (
                        <span className={`font-mono text-lg ${
                          item.stock_status === 'out_of_stock' ? 'text-red-600 font-bold' :
                          item.stock_status === 'low_stock' ? 'text-amber-600 font-bold' :
                          item.stock_count === null ? 'text-slate-400' : ''
                        }`}>
                          {item.stock_count ?? '∞'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {getStatusBadge(item.stock_status)}
                    </TableCell>
                    <TableCell className="text-right">
                      {editingId === item.id ? (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            onClick={() => handleSaveStock(item.id)}
                            disabled={saving}
                            data-testid={`save-stock-${item.id}`}
                          >
                            <Save className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCancelEdit}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStartEdit(item)}
                            data-testid={`edit-stock-${item.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {item.stock_count !== null && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSetUnlimited(item.id)}
                              title={isSpanish ? 'Establecer ilimitado' : 'Set to unlimited'}
                            >
                              ∞
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteItem(item.id, item.name)}
                            title={isSpanish ? 'Eliminar item' : 'Delete item'}
                            data-testid={`delete-item-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* AI Receipt Scan Dialog */}
      <Dialog open={showScanDialog} onOpenChange={setShowScanDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              {isSpanish ? 'Escanear Recibo con AI' : 'AI Receipt Scanner'}
            </DialogTitle>
            <DialogDescription>
              {isSpanish 
                ? 'La AI analizará el recibo y extraerá los productos para añadir al stock'
                : 'AI will analyze the receipt and extract products to add to stock'}
            </DialogDescription>
          </DialogHeader>
          
          {!scanResults ? (
            <div className="space-y-4">
              {scanPreview && (
                <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-slate-100">
                  <img src={scanPreview} alt="Receipt" className="w-full h-full object-contain" />
                </div>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setShowScanDialog(false);
                  setScanImage(null);
                  setScanPreview(null);
                }}>
                  {isSpanish ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button onClick={handleScanReceipt} disabled={scanning}>
                  {scanning ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {isSpanish ? 'Analizando...' : 'Analyzing...'}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {isSpanish ? 'Escanear' : 'Scan'}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-800 mb-2">
                  {isSpanish ? 'Productos encontrados:' : 'Products found:'}
                </h4>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {scanResults.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white rounded p-2">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-slate-500">{item.category || 'Sin categoría'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono">{item.quantity}x</p>
                        {item.price && <p className="text-sm text-slate-500">{formatPrice(item.price)}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setScanResults(null);
                  setScanImage(null);
                  setScanPreview(null);
                }}>
                  {isSpanish ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button variant="outline" onClick={() => setScanResults({ ...scanResults, editing: true })}>
                  {isSpanish ? 'Editar' : 'Edit'}
                </Button>
                <Button onClick={handleMergeStock} disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ShoppingCart className="w-4 h-4 mr-2" />
                  )}
                  {isSpanish ? 'Añadir al Stock' : 'Add to Stock'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Drinks Catalog Dialog */}
      <Dialog open={showDrinksDialog} onOpenChange={setShowDrinksDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wine className="w-5 h-5 text-orange-500" />
              {isSpanish ? 'Catálogo de Bebidas' : 'Drinks Catalog'}
            </DialogTitle>
            <DialogDescription>
              {catalogMode === 'stock'
                ? (isSpanish 
                    ? 'Añade productos a tu inventario. Usa tamaños de compra (botellas, packs).'
                    : 'Add products to your inventory. Use purchase sizes (bottles, packs).')
                : (isSpanish 
                    ? 'Añade bebidas a tu menú. Usa tamaños de servicio (copa, chupito).'
                    : 'Add drinks to your menu. Use serving sizes (glass, shot).')
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Mode Toggle & Custom Item Button */}
          <div className="flex items-center justify-between border-b pb-3 mb-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">{isSpanish ? 'Modo:' : 'Mode:'}</span>
              <div className="flex rounded-lg border overflow-hidden">
                <button
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${catalogMode === 'stock' ? 'bg-orange-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  onClick={() => { setCatalogMode('stock'); setSelectedDrinks([]); }}
                >
                  {isSpanish ? '📦 Stock' : '📦 Stock'}
                </button>
                <button
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${catalogMode === 'menu' ? 'bg-orange-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  onClick={() => { setCatalogMode('menu'); setSelectedDrinks([]); }}
                >
                  {isSpanish ? '📋 Menú' : '📋 Menu'}
                </button>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowCustomItemDialog(true)}>
              <Plus className="w-4 h-4 mr-1" />
              {isSpanish ? 'Item Personalizado' : 'Custom Item'}
            </Button>
          </div>
          
          <Tabs value={drinksCategory} onValueChange={setDrinksCategory} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid grid-cols-6 mb-4">
              {Object.entries(drinksCatalog).map(([key, cat]) => {
                const Icon = cat.icon;
                return (
                  <TabsTrigger key={key} value={key} className="flex items-center gap-1">
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{cat.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
            
            {Object.entries(drinksCatalog).map(([key, cat]) => (
              <TabsContent key={key} value={key} className="flex-1 overflow-y-auto">
                <div className="space-y-3">
                  {cat.items.map((drink, idx) => {
                    const editedDrink = getDrinkWithEdits(drink);
                    const drinkImage = getDrinkImage(editedDrink, key);
                    const DrinkIcon = getDrinkIcon(editedDrink, cat.label);
                    
                    return (
                      <Card key={idx} className="p-3 hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3">
                          {/* Drink Image or Placeholder Icon */}
                          <div className="relative flex-shrink-0">
                            {drinkImage ? (
                              <img 
                                src={drinkImage} 
                                alt={editedDrink.name} 
                                className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div 
                              className={`w-12 h-12 rounded-lg bg-gradient-to-br ${
                                key === 'beer' ? 'from-amber-100 to-amber-200' :
                                key === 'wine' ? 'from-purple-100 to-purple-200' :
                                key === 'spirits' ? 'from-slate-100 to-slate-200' :
                                key === 'cocktails' ? 'from-pink-100 to-pink-200' :
                                key === 'coffee' ? 'from-orange-100 to-orange-200' :
                                'from-blue-100 to-blue-200'
                              } flex items-center justify-center border border-slate-200`}
                              style={{ display: drinkImage ? 'none' : 'flex' }}
                            >
                              <DrinkIcon className={`w-6 h-6 ${
                                key === 'beer' ? 'text-amber-600' :
                                key === 'wine' ? 'text-purple-600' :
                                key === 'spirits' ? 'text-slate-600' :
                                key === 'cocktails' ? 'text-pink-600' :
                                key === 'coffee' ? 'text-orange-600' :
                                'text-blue-600'
                              }`} />
                            </div>
                          </div>
                          
                          {/* Drink Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-sm truncate">{editedDrink.name}</h4>
                              {customCatalogItems[drink.name] && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                                  {isSpanish ? 'Editado' : 'Edited'}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 truncate">{editedDrink.category}</p>
                            {catalogMode === 'menu' && editedDrink.ingredients && (
                              <p className="text-xs text-slate-400 truncate italic">{editedDrink.ingredients}</p>
                            )}
                          </div>
                          
                          {/* Sizes */}
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {(catalogMode === 'stock' && editedDrink.stockSizes 
                              ? editedDrink.stockSizes 
                              : catalogMode === 'menu' && editedDrink.menuSizes 
                                ? editedDrink.menuSizes 
                                : editedDrink.sizes || []
                            ).slice(0, 4).map(size => {
                              const isSelected = selectedDrinks.some(d => d.key === `${editedDrink.name}-${size}`);
                              return (
                                <Badge
                                  key={size}
                                  variant={isSelected ? 'default' : 'outline'}
                                  className={`cursor-pointer text-xs ${isSelected ? 'bg-orange-500' : 'hover:bg-orange-50'}`}
                                  onClick={() => handleDrinkToggle(editedDrink, size)}
                                >
                                  {size}
                                </Badge>
                              );
                            })}
                            {(editedDrink.sizes || []).length > 4 && (
                              <Badge variant="outline" className="text-xs">
                                +{(editedDrink.sizes || []).length - 4}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Edit Button - Always Visible */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditDrink(drink, key)}
                            className="flex-shrink-0 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                            title={isSpanish ? 'Editar bebida' : 'Edit drink'}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>
            ))}
          </Tabs>
          
          <DialogFooter className="border-t pt-4">
            <div className="flex-1 text-sm text-slate-500">
              {selectedDrinks.length} {isSpanish ? 'items seleccionados' : 'items selected'}
            </div>
            <Button variant="outline" onClick={() => setShowDrinksDialog(false)}>
              {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleAddDrinksToMenu} disabled={selectedDrinks.length === 0 || addingDrinks}>
              {addingDrinks ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              {isSpanish ? 'Añadir al Menú' : 'Add to Menu'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Drink Dialog */}
      <Dialog open={showEditDrinkDialog} onOpenChange={setShowEditDrinkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-500" />
              {isSpanish ? 'Editar Bebida' : 'Edit Drink'}
            </DialogTitle>
            <DialogDescription>
              {isSpanish ? 'Modifica los detalles de esta bebida' : 'Modify this drink\'s details'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Image Preview & Upload */}
            <div className="flex items-center gap-4">
              <div className="relative">
                {editDrinkForm.image ? (
                  <img 
                    src={editDrinkForm.image} 
                    alt={editDrinkForm.name}
                    className="w-20 h-20 rounded-lg object-cover border-2 border-slate-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center border-2 border-dashed border-slate-300">
                    <Image className="w-8 h-8 text-slate-400" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={editDrinkImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleEditDrinkImageUpload(file);
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => editDrinkImageInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {isSpanish ? 'Subir Imagen' : 'Upload Image'}
                </Button>
                <Input
                  placeholder={isSpanish ? 'O pegar URL de imagen' : 'Or paste image URL'}
                  value={editDrinkForm.image}
                  onChange={(e) => setEditDrinkForm(prev => ({ ...prev, image: e.target.value }))}
                  className="text-xs"
                />
              </div>
            </div>

            {/* Name */}
            <div>
              <Label>{isSpanish ? 'Nombre' : 'Name'} *</Label>
              <Input
                value={editDrinkForm.name}
                onChange={(e) => setEditDrinkForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder={isSpanish ? 'Nombre de la bebida' : 'Drink name'}
              />
            </div>

            {/* Category */}
            <div>
              <Label>{isSpanish ? 'Categoría' : 'Category'}</Label>
              <Input
                value={editDrinkForm.category}
                onChange={(e) => setEditDrinkForm(prev => ({ ...prev, category: e.target.value }))}
                placeholder={isSpanish ? 'Ej: Cervezas Premium' : 'E.g. Premium Beers'}
              />
            </div>

            {/* Sizes */}
            <div>
              <Label>{isSpanish ? 'Tamaños (separados por coma)' : 'Sizes (comma separated)'}</Label>
              <Input
                value={editDrinkForm.sizes}
                onChange={(e) => setEditDrinkForm(prev => ({ ...prev, sizes: e.target.value }))}
                placeholder={isSpanish ? 'Ej: Caña, Tubo, Jarra, 33cl' : 'E.g. Small, Medium, Large, 33cl'}
              />
              <p className="text-xs text-slate-400 mt-1">
                {isSpanish ? 'Los tamaños aparecerán como opciones seleccionables' : 'Sizes will appear as selectable options'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditDrinkDialog(false);
              setEditingDrinkItem(null);
            }}>
              {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSaveEditedDrink}
              disabled={savingDrink || !editDrinkForm.name.trim()}
            >
              {savingDrink ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isSpanish ? 'Guardar' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Credits Dialog */}
      <Dialog open={showCreditsDialog} onOpenChange={setShowCreditsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-500" />
              {isSpanish ? 'Créditos AI' : 'AI Credits'}
            </DialogTitle>
            <DialogDescription>
              {isSpanish 
                ? 'Usa créditos AI para escanear recibos y menús automáticamente'
                : 'Use AI credits to scan receipts and menus automatically'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Card className="bg-slate-50">
              <CardContent className="pt-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-slate-500">{isSpanish ? 'Créditos Disponibles' : 'Available Credits'}</p>
                    <p className="text-3xl font-bold">{remainingCredits}</p>
                  </div>
                  <div className="text-right text-sm text-slate-500">
                    <p>{aiCredits.limit} {isSpanish ? 'gratuitos/mes' : 'free/month'}</p>
                    <p>{aiCredits.purchased} {isSpanish ? 'comprados' : 'purchased'}</p>
                    <p>{aiCredits.used} {isSpanish ? 'usados' : 'used'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-3">
              <h4 className="font-medium">{isSpanish ? 'Comprar Créditos' : 'Buy Credits'}</h4>
              
              <Card className="cursor-pointer hover:border-emerald-500 transition-colors" onClick={() => window.open(`/buy-credits?pack=5&restaurant=${selectedRestaurant.id}`, '_blank')}>
                <CardContent className="pt-4 flex justify-between items-center">
                  <div>
                    <p className="font-medium">5 {isSpanish ? 'escaneos' : 'scans'}</p>
                    <p className="text-sm text-slate-500">{isSpanish ? 'Para uso ocasional' : 'For occasional use'}</p>
                  </div>
                  <p className="text-xl font-bold">{currencyConfig.symbol}5</p>
                </CardContent>
              </Card>
              
              <Card className="cursor-pointer hover:border-emerald-500 transition-colors border-emerald-200 bg-emerald-50" onClick={() => window.open(`/buy-credits?pack=15&restaurant=${selectedRestaurant.id}`, '_blank')}>
                <CardContent className="pt-4 flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">15 {isSpanish ? 'escaneos' : 'scans'}</p>
                      <Badge className="bg-emerald-500">20% OFF</Badge>
                    </div>
                    <p className="text-sm text-slate-500">{isSpanish ? 'Más popular' : 'Most popular'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold">{currencyConfig.symbol}12</p>
                    <p className="text-sm text-slate-500 line-through">{currencyConfig.symbol}15</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="cursor-pointer hover:border-emerald-500 transition-colors" onClick={() => window.open(`/buy-credits?pack=50&restaurant=${selectedRestaurant.id}`, '_blank')}>
                <CardContent className="pt-4 flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">50 {isSpanish ? 'escaneos' : 'scans'}</p>
                      <Badge className="bg-orange-500">30% OFF</Badge>
                    </div>
                    <p className="text-sm text-slate-500">{isSpanish ? 'Mejor valor' : 'Best value'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold">{currencyConfig.symbol}35</p>
                    <p className="text-sm text-slate-500 line-through">{currencyConfig.symbol}50</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tax Management Dialog */}
      <Dialog open={showTaxDialog} onOpenChange={setShowTaxDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-blue-500" />
              {isSpanish ? 'Gestión de Impuestos' : 'Tax Management'}
            </DialogTitle>
            <DialogDescription>
              {isSpanish 
                ? 'Configura tasas de impuestos para productos y categorías'
                : 'Configure tax rates for products and categories'}
            </DialogDescription>
          </DialogHeader>
          
          <TaxManagement 
            restaurantId={selectedRestaurant.id} 
            api={api} 
            isSpanish={isSpanish}
            onClose={() => setShowTaxDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Custom Item Dialog */}
      <Dialog open={showCustomItemDialog} onOpenChange={setShowCustomItemDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-500" />
              {isSpanish ? 'Añadir Item Personalizado' : 'Add Custom Item'}
            </DialogTitle>
            <DialogDescription>
              {catalogMode === 'stock'
                ? (isSpanish ? 'Añade un producto personalizado a tu inventario' : 'Add a custom product to your inventory')
                : (isSpanish ? 'Añade una bebida personalizada a tu menú' : 'Add a custom drink to your menu')
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            <div>
              <Label>{isSpanish ? 'Nombre del Producto' : 'Product Name'} *</Label>
              <Input 
                value={customItem.name}
                onChange={(e) => setCustomItem({ ...customItem, name: e.target.value })}
                placeholder={isSpanish ? 'Ej: Cerveza Artesana Local' : 'E.g. Local Craft Beer'}
              />
            </div>
            <div>
              <Label>{isSpanish ? 'Categoría' : 'Category'}</Label>
              <Input 
                value={customItem.category}
                onChange={(e) => setCustomItem({ ...customItem, category: e.target.value })}
                placeholder={isSpanish ? 'Ej: Cervezas Especiales' : 'E.g. Special Beers'}
              />
            </div>
            <div>
              <Label>{catalogMode === 'stock' ? (isSpanish ? 'Tamaño de Compra' : 'Purchase Size') : (isSpanish ? 'Tamaño de Servicio' : 'Serving Size')}</Label>
              <Input 
                value={customItem.size}
                onChange={(e) => setCustomItem({ ...customItem, size: e.target.value })}
                placeholder={catalogMode === 'stock' ? (isSpanish ? 'Ej: Botella 75cl, Pack 6' : 'E.g. Bottle 75cl, 6-Pack') : (isSpanish ? 'Ej: Copa, Vaso' : 'E.g. Glass, Cup')}
              />
            </div>
            {catalogMode === 'menu' && (
              <div>
                <Label>{isSpanish ? `Precio (${currencyConfig.symbol})` : `Price (${currencyConfig.symbol})`}</Label>
                <Input 
                  type="number"
                  value={customItem.price}
                  onChange={(e) => setCustomItem({ ...customItem, price: e.target.value })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
            )}
          </div>
          
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => {
              setShowCustomItemDialog(false);
              setCustomItem({ name: '', category: '', size: '', price: '' });
            }}>
              {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={async () => {
                if (!customItem.name || !customItem.size) {
                  toast.error(isSpanish ? 'Nombre y tamaño son obligatorios' : 'Name and size are required');
                  return;
                }
                try {
                  const itemToAdd = {
                    name: `${customItem.name} (${customItem.size})`,
                    category: customItem.category || (catalogMode === 'stock' ? 'Stock Personalizado' : 'Bebidas Personalizadas'),
                    price: customItem.price ? parseFloat(customItem.price) : null,
                    needs_price: !customItem.price,
                    is_for_stock: catalogMode === 'stock'
                  };
                  
                  await api.post(`/restaurants/${selectedRestaurant.id}/menu/add-drinks`, {
                    drinks: [itemToAdd]
                  });
                  
                  toast.success(isSpanish ? 'Item añadido' : 'Item added');
                  setShowCustomItemDialog(false);
                  setCustomItem({ name: '', category: '', size: '', price: '' });
                  fetchInventory();
                } catch (error) {
                  toast.error(isSpanish ? 'Error al añadir item' : 'Failed to add item');
                }
              }}
              disabled={!customItem.name || !customItem.size}
            >
              <Plus className="w-4 h-4 mr-2" />
              {isSpanish ? 'Añadir Item' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drink Image Edit Dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-blue-500" />
              {isSpanish ? 'Editar Imagen de Bebida' : 'Edit Drink Image'}
            </DialogTitle>
            <DialogDescription>
              {editingDrink?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            {/* Current Image Preview */}
            <div className="flex justify-center">
              {drinkImageUrl ? (
                <img 
                  src={drinkImageUrl} 
                  alt={editingDrink?.name} 
                  className="w-32 h-32 rounded-lg object-cover border-2 border-slate-200"
                  onError={(e) => {
                    e.target.src = '';
                    e.target.alt = 'Invalid image URL';
                  }}
                />
              ) : (
                <div className="w-32 h-32 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center border-2 border-dashed border-slate-300">
                  <Image className="w-12 h-12 text-slate-400" />
                </div>
              )}
            </div>

            {/* Upload Image */}
            <div>
              <Label className="mb-2 block">{isSpanish ? 'Subir Imagen' : 'Upload Image'}</Label>
              <input
                ref={drinkImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleDrinkImageUpload(file);
                }}
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => drinkImageInputRef.current?.click()}
                disabled={uploadingImage}
              >
                {uploadingImage ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {isSpanish ? 'Seleccionar Archivo' : 'Select File'}
              </Button>
            </div>

            {/* Or use URL */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">{isSpanish ? 'O usar URL' : 'Or use URL'}</span>
              </div>
            </div>

            <div>
              <Label>{isSpanish ? 'URL de Imagen' : 'Image URL'}</Label>
              <Input 
                value={drinkImageUrl}
                onChange={(e) => setDrinkImageUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => {
              setShowImageDialog(false);
              setEditingDrink(null);
              setDrinkImageUrl('');
            }}>
              {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSaveDrinkImageUrl}
              disabled={!drinkImageUrl || uploadingImage}
            >
              {uploadingImage ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isSpanish ? 'Guardar' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Tax Management Component
function TaxManagement({ restaurantId, api, isSpanish, onClose }) {
  const [taxRates, setTaxRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTax, setNewTax] = useState({ name: '', rate: '', applies_to: 'all' });
  const [saving, setSaving] = useState(false);

  // Preset tax rates
  const presetRates = {
    es: [
      { name: 'IVA General (21%)', rate: 21, description: 'Alcohol, servicios' },
      { name: 'IVA Reducido (10%)', rate: 10, description: 'Comida, bebidas sin alcohol' },
      { name: 'IVA Superreducido (4%)', rate: 4, description: 'Pan, leche, huevos' },
      { name: 'Exento (0%)', rate: 0, description: 'Productos exentos' },
    ],
    uk: [
      { name: 'Standard VAT (20%)', rate: 20, description: 'Standard rate' },
      { name: 'Reduced VAT (5%)', rate: 5, description: 'Some food, energy' },
      { name: 'Zero Rate (0%)', rate: 0, description: 'Most food, children items' },
    ]
  };

  useEffect(() => {
    fetchTaxRates();
  }, []);

  const fetchTaxRates = async () => {
    try {
      const response = await api.get(`/restaurants/${restaurantId}/tax-rates`);
      setTaxRates(response.data);
    } catch (error) {
      setTaxRates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPreset = async (preset) => {
    try {
      await api.post(`/restaurants/${restaurantId}/tax-rates`, {
        name: preset.name,
        rate: preset.rate,
        applies_to: 'all'
      });
      toast.success(isSpanish ? 'Tasa añadida' : 'Tax rate added');
      fetchTaxRates();
    } catch (error) {
      toast.error(isSpanish ? 'Error al añadir' : 'Failed to add');
    }
  };

  const handleAddCustom = async () => {
    if (!newTax.name || newTax.rate === '') return;
    
    try {
      setSaving(true);
      await api.post(`/restaurants/${restaurantId}/tax-rates`, {
        name: newTax.name,
        rate: parseFloat(newTax.rate),
        applies_to: newTax.applies_to
      });
      toast.success(isSpanish ? 'Tasa añadida' : 'Tax rate added');
      setNewTax({ name: '', rate: '', applies_to: 'all' });
      setShowAddForm(false);
      fetchTaxRates();
    } catch (error) {
      toast.error(isSpanish ? 'Error al añadir' : 'Failed to add');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (taxId) => {
    try {
      await api.delete(`/restaurants/${restaurantId}/tax-rates/${taxId}`);
      toast.success(isSpanish ? 'Tasa eliminada' : 'Tax rate deleted');
      fetchTaxRates();
    } catch (error) {
      toast.error(isSpanish ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  return (
    <div className="space-y-4">
      {/* Current Tax Rates */}
      <div>
        <h4 className="font-medium mb-2">{isSpanish ? 'Tasas Actuales' : 'Current Tax Rates'}</h4>
        {loading ? (
          <p className="text-slate-500 text-sm">{isSpanish ? 'Cargando...' : 'Loading...'}</p>
        ) : taxRates.length === 0 ? (
          <p className="text-slate-500 text-sm">{isSpanish ? 'No hay tasas configuradas' : 'No tax rates configured'}</p>
        ) : (
          <div className="space-y-2">
            {taxRates.map(tax => (
              <div key={tax.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                <div>
                  <p className="font-medium">{tax.name}</p>
                  <p className="text-sm text-slate-500">{tax.applies_to === 'all' ? (isSpanish ? 'Todos los productos' : 'All products') : tax.applies_to}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge>{tax.rate}%</Badge>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(tax.id)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preset Rates */}
      <div>
        <h4 className="font-medium mb-2">{isSpanish ? 'Tasas Predefinidas' : 'Preset Tax Rates'}</h4>
        <Tabs defaultValue="es">
          <TabsList>
            <TabsTrigger value="es">🇪🇸 España</TabsTrigger>
            <TabsTrigger value="uk">🇬🇧 UK</TabsTrigger>
          </TabsList>
          
          <TabsContent value="es" className="mt-2">
            <div className="grid grid-cols-2 gap-2">
              {presetRates.es.map(preset => (
                <Card key={preset.name} className="cursor-pointer hover:border-blue-500 transition-colors" onClick={() => handleAddPreset(preset)}>
                  <CardContent className="pt-3 pb-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-sm">{preset.name}</p>
                        <p className="text-xs text-slate-500">{preset.description}</p>
                      </div>
                      <Plus className="w-4 h-4 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="uk" className="mt-2">
            <div className="grid grid-cols-2 gap-2">
              {presetRates.uk.map(preset => (
                <Card key={preset.name} className="cursor-pointer hover:border-blue-500 transition-colors" onClick={() => handleAddPreset(preset)}>
                  <CardContent className="pt-3 pb-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-sm">{preset.name}</p>
                        <p className="text-xs text-slate-500">{preset.description}</p>
                      </div>
                      <Plus className="w-4 h-4 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Custom Rate Form */}
      {showAddForm ? (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{isSpanish ? 'Nombre' : 'Name'}</Label>
                <Input 
                  value={newTax.name} 
                  onChange={(e) => setNewTax({ ...newTax, name: e.target.value })}
                  placeholder={isSpanish ? 'Ej: IVA Especial' : 'E.g. Special VAT'}
                />
              </div>
              <div>
                <Label>{isSpanish ? 'Tasa (%)' : 'Rate (%)'}</Label>
                <Input 
                  type="number"
                  value={newTax.rate} 
                  onChange={(e) => setNewTax({ ...newTax, rate: e.target.value })}
                  placeholder="0"
                  min="0"
                  max="100"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddForm(false)} className="flex-1">
                {isSpanish ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button onClick={handleAddCustom} disabled={saving} className="flex-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isSpanish ? 'Añadir' : 'Add')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button variant="outline" className="w-full" onClick={() => setShowAddForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {isSpanish ? 'Añadir Tasa Personalizada' : 'Add Custom Tax Rate'}
        </Button>
      )}
    </div>
  );
}
